import { useQuery } from "@apollo/client";
import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  SxProps,
  TextFieldProps,
  Theme,
  Typography,
} from "@mui/material";
import { keyBy, omit, upperFirst } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import {
  Control,
  Path,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";

import { STATES } from "@stw/states";
import {
  AudienceTargetingFieldType,
  AudienceTargetingFilterCategory,
  AudienceTargetingFilterDonationHistoryOperations,
  AudienceTargetingFilterOperation,
  TargetingForm,
  TargetingFormFilter,
  TargetingFormSchema,
} from "@stw/yup-validators";

import {
  AudienceContactFieldType,
  AudienceTargetingFilterCategoryInput,
  GetAudienceContactFieldsQuery,
  GetAudienceContactFieldsQueryVariables,
  ImportCampaignContactsFromSubscribersMutationVariables,
  ListAudienceSearchFiltersQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import {
  RhfZipCodeRadius,
  RhfCommaSeparatedTextField,
  RhfCurrencyField,
  RhfDatePicker,
  RhfNumericalRange,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { RhfNumberField } from "~frontend/components/forms/react-hook-form-components/RhfNumberField";
import {
  GET_AUDIENCE_CONTACT_FIELDS,
  LIST_AUDIENCE_SEARCH_FILTERS,
} from "~frontend/graphql/audience";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { generateTargetGroupSentence } from "~frontend/util/generateTargetGroupSentence";

import { AsyncWrapper } from "../AsyncWrapper";
import CheckBoxUnchecked from "../icons/CheckBoxUnchecked";

// zod enum -> GraphQL enum
function convertFilterCategoryToGraphQL(
  category: AudienceTargetingFilterCategory
): AudienceTargetingFilterCategoryInput {
  switch (category) {
    case AudienceTargetingFilterCategory.Location:
      return AudienceTargetingFilterCategoryInput.Location;
    case AudienceTargetingFilterCategory.DonationHistory:
      return AudienceTargetingFilterCategoryInput.DonationHistory;
    case AudienceTargetingFilterCategory.CustomField:
      return AudienceTargetingFilterCategoryInput.CustomField;
    case AudienceTargetingFilterCategory.SavedSearch:
      return AudienceTargetingFilterCategoryInput.SavedSearch;
    default:
      throw new Error(`Unknown filter category: ${category}`);
  }
}

// Convert TargetingForm to GraphQL input format
export function convertTargetingFormToGraphQL(
  form: TargetingForm
): ImportCampaignContactsFromSubscribersMutationVariables["input"]["filters"] {
  return {
    targetGroups: form.targetGroups.map(group => ({
      clauses: group.clauses.map(clause => ({
        values: clause.values ?? undefined,
        value: clause.value ?? undefined,
        fieldType: clause.fieldType,
        filterOperation: clause.filterOperation,
        filterConfig: clause.filterConfig
          ? omit(clause.filterConfig, "__typename")
          : undefined,
        fieldName: clause.fieldName ?? undefined,
        filterCategory: convertFilterCategoryToGraphQL(clause.filterCategory),
      })),
      dateFilter: group.dateFilter
        ? {
            key: group.dateFilter.key,
            startDate:
              typeof group.dateFilter.startDate === "string"
                ? group.dateFilter.startDate
                : group.dateFilter.startDate?.toISO(),
            endDate:
              typeof group.dateFilter.endDate === "string"
                ? group.dateFilter.endDate
                : group.dateFilter.endDate?.toISO(),
          }
        : undefined,
    })),
  };
}

export const DEFAULT_TARGET = {
  clauses: [
    {
      filterCategory: "location",
      filterOperation: null,
      fieldType: null,
      fieldName: null,
      values: [],
    },
  ],
};

type FilterConfig = {
  label: string;
  items: {
    label: string;
    value: AudienceTargetingFilterOperation;
    fieldType: AudienceTargetingFieldType;
    allowDateFiltering?: boolean;
  }[];
};

type FilterHierarchy = {
  [K in AudienceTargetingFilterCategory]: FilterConfig;
};

function customFieldOperations(
  customFields: GetAudienceContactFieldsQuery["getAudienceContactFields"]
) {
  return customFields
    .filter(field => field.isEditable)
    .reduce(
      (acc, field) => {
        switch (field.type) {
          case AudienceContactFieldType.String:
            acc[field.name] = [
              {
                label: "contains text",
                value: "contains",
                fieldType: "TEXT",
              },
              {
                label: "matches",
                value: "matches",
                fieldType: "TEXT",
              },
              {
                label: "does not contain text",
                value: "notContains",
                fieldType: "TEXT",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Number:
            acc[field.name] = [
              {
                label: "is greater than",
                value: "gt",
                fieldType: field.type,
              },
              {
                label: "is less than",
                value: "lt",
                fieldType: field.type,
              },
              {
                label: "is equal to",
                value: "equals",
                fieldType: field.type,
              },
              {
                label: "is not equal to",
                value: "notEquals",
                fieldType: field.type,
              },
              {
                label: "is between",
                value: "between",
                fieldType: "NUMBER_RANGE",
              },
              {
                label: "is not between",
                value: "notBetween",
                fieldType: "NUMBER_RANGE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Boolean:
            acc[field.name] = [
              {
                label: "is true",
                value: "customTrue",
                fieldType: "BOOLEAN_TRUE",
              },
              {
                label: "is false",
                value: "customFalse",
                fieldType: "BOOLEAN_FALSE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Enum:
            acc[field.name] = [
              {
                label: "is one of",
                value: "oneOf",
                fieldType: "ENUM",
              },
              {
                label: "is not one of",
                value: "notOneOf",
                fieldType: "ENUM",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.State:
            acc[field.name] = [
              {
                label: "is in state",
                value: "isInCustomState",
                fieldType: "STATE",
              },
              {
                label: "is not in state",
                value: "notInCustomState",
                fieldType: "STATE",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          case AudienceContactFieldType.Zip:
            acc[field.name] = [
              {
                label: "is in zip code",
                value: "isInCustomZip",
                fieldType: "ZIP",
              },
              {
                label: "is not in zip code",
                value: "notInCustomZip",
                fieldType: "ZIP",
              },
              {
                label: "is in zip code radius",
                value: "isInCustomZipRadius",
                fieldType: "ZIP_RADIUS",
              },
              {
                label: "is not in zip code radius",
                value: "notInCustomZipRadius",
                fieldType: "ZIP_RADIUS",
              },
              {
                label: "is empty",
                value: "customIsNull",
                fieldType: "IS_NULL",
              },
            ];
            break;
          default:
            acc[field.name] = [];
        }
        return acc;
      },
      {} as Record<
        string,
        {
          label: string;
          value: string | number;
          fieldType: string;
          allowDateFiltering?: boolean;
        }[]
      >
    );
}

export function availableTargetingFilters({
  includeDonationHistory,
  includeCustomFields,
  includeSavedSearches,
}: {
  includeDonationHistory: boolean;
  includeCustomFields: boolean;
  includeSavedSearches: boolean;
}): Partial<FilterHierarchy> {
  const FILTER_HIERARCHY: Partial<FilterHierarchy> = {
    location: {
      label: "Location",
      items: [
        {
          label: "is in state",
          value: "isInState",
          fieldType: "STATE",
        },
        {
          label: "is not in state",
          value: "notInState",
          fieldType: "STATE",
        },
        {
          label: "state is empty",
          value: "stateIsNull",
          fieldType: "IS_NULL",
        },
        {
          label: "is in zip code",
          value: "isInZip",
          fieldType: "ZIP",
        },
        {
          label: "is not in zip code",
          value: "notInZip",
          fieldType: "ZIP",
        },
        {
          label: "is in zip code radius",
          value: "isInZipRadius",
          fieldType: "ZIP_RADIUS",
        },
        {
          label: "is not in zip code radius",
          value: "notInZipRadius",
          fieldType: "ZIP_RADIUS",
        },
        {
          label: "zip is empty",
          value: "zipIsNull",
          fieldType: "IS_NULL",
        },
      ],
    },
  };

  if (includeDonationHistory) {
    FILTER_HIERARCHY.donationHistory = {
      label: "Donation History",
      items: [
        {
          label: "total contributions are greater than",
          value: "totalDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "total contributions are less than",
          value: "totalDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "total contributions are between range",
          value: "totalDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "is a recurring donor",
          value: "isRecurringDonor",
          fieldType: "BOOLEAN_TRUE",
        },
        {
          label: "is a recurring donor with recurring amount greater than",
          value: "recurringDonationAmountGt",
          fieldType: "CURRENCY",
        },
        {
          label: "is a recurring donor with recurring amount less than",
          value: "recurringDonationAmountLt",
          fieldType: "CURRENCY",
        },
        {
          label: "is a recurring donor with recurring amount between range",
          value: "recurringDonationAmountBetween",
          fieldType: "CURRENCY_RANGE",
        },
        {
          label: "is not a recurring donor",
          value: "notRecurringDonor",
          fieldType: "BOOLEAN_FALSE",
        },
        {
          label: "has made a donation",
          value: "hasDonated",
          fieldType: "BOOLEAN_TRUE",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation",
          value: "notHasDonated",
          fieldType: "BOOLEAN_FALSE",
          allowDateFiltering: true,
        },
        {
          label: "has made a donation on a mobile device",
          value: "hasMobileDonation",
          fieldType: "BOOLEAN_TRUE",
          allowDateFiltering: true,
        },
        {
          label: "has not made a donation on a mobile device",
          value: "notHasMobileDonation",
          fieldType: "BOOLEAN_FALSE",
          allowDateFiltering: true,
        },
        {
          label: "last donation was greater than",
          value: "lastDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "last donation was less than",
          value: "lastDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "last donation was between range",
          value: "lastDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is greater than",
          value: "numberOfDonationsGt",
          fieldType: "NUMBER",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is less than",
          value: "numberOfDonationsLt",
          fieldType: "NUMBER",
          allowDateFiltering: true,
        },
        {
          label: "number of donations is between range",
          value: "numberOfDonationsBetween",
          fieldType: "NUMBER_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "average donation is greater than",
          value: "averageDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "average donation is less than",
          value: "averageDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "average donation is between range",
          value: "averageDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is greater than",
          value: "highestDonationGt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is less than",
          value: "highestDonationLt",
          fieldType: "CURRENCY",
          allowDateFiltering: true,
        },
        {
          label: "highest donation is between range",
          value: "highestDonationBetween",
          fieldType: "CURRENCY_RANGE",
          allowDateFiltering: true,
        },
      ],
    };
  }

  if (includeCustomFields) {
    FILTER_HIERARCHY.customField = {
      label: "Custom Field",
      items: [],
    };
  }

  if (includeSavedSearches) {
    FILTER_HIERARCHY.savedSearch = {
      label: "Saved Search",
      items: [
        {
          label: "is in saved search audience",
          value: "isInSavedSearch",
          fieldType: "SAVED_SEARCH",
          allowDateFiltering: false,
        },
      ],
    };
  }

  return FILTER_HIERARCHY;
}

const DATE_FILTER_RANGES = [
  { key: "ALL_TIME", label: "All time", start: null, end: null },
  {
    key: "LAST_YEAR",
    label: "Last year",
    start: DateTime.now().minus({ years: 1 }).startOf("year"),
    end: DateTime.now().minus({ years: 1 }).endOf("year"),
  },
  {
    key: "THIS_YEAR",
    label: "This year",
    start: DateTime.now().startOf("year"),
    end: DateTime.now().endOf("day"),
  },
  {
    key: "THIS_MONTH",
    label: "This month",
    start: DateTime.now().startOf("month"),
    end: DateTime.now().endOf("day"),
  },
  {
    key: "LAST_MONTH",
    label: "Last month",
    start: DateTime.now().minus({ months: 1 }).startOf("month"),
    end: DateTime.now().minus({ months: 1 }).endOf("month"),
  },
  {
    key: "CUSTOM_DATE_RANGE",
    label: "Specific date range",
    start: DateTime.now().minus({ days: 1 }).startOf("day"),
    end: DateTime.now().endOf("day"),
  },
];

const DATE_FILTER_RANGE_BY_KEY = keyBy(DATE_FILTER_RANGES, "key");

const FIELD_TYPE_TO_INPUT: {
  [key in AudienceTargetingFieldType]: {
    label?: string;
    nameSuffix: "value" | "values";
    bottomLabel?: string;
    slimVariant?: "underlined" | "outlined" | "standard";
    defaultVariant?: "underlined" | "outlined";
    slimSx?: SxProps<Theme>;
    defaultSx?: SxProps<Theme>;
    slimSlotProps?: TextFieldProps["slotProps"];
  };
} = {
  STATE: {
    label: "States",
    nameSuffix: "values",
    bottomLabel: "You may select multiple states.",
    slimVariant: "underlined",
    defaultVariant: "outlined",
    slimSx: {
      minWidth: "160px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
  },
  NUMBER: {
    label: "Amount",
    nameSuffix: "value",
    bottomLabel: "Enter amount",
    slimVariant: "standard",
    slimSx: { minWidth: "160px" },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  CURRENCY: {
    label: "Amount",
    nameSuffix: "value",
    bottomLabel: "Enter amount",
    slimVariant: "standard",
    slimSx: { minWidth: "160px" },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  TEXT: {
    label: "Text Value",
    nameSuffix: "value",
    bottomLabel: "Enter text",
    slimVariant: "standard",
    slimSx: { minWidth: "160px", fontSize: theme.spacing(1.5) },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  ZIP: {
    label: "Zip Codes",
    nameSuffix: "values",
    slimVariant: "standard",
    bottomLabel: "Enter zip codes separated by commas",
    slimSx: {
      minWidth: "200px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
    slimSlotProps: {
      input: { sx: { fontSize: "12px" } },
    },
  },
  ZIP_RADIUS: {
    nameSuffix: "values",
  },
  ENUM: {
    label: "Value(s)",
    nameSuffix: "values",
    slimVariant: "underlined",
    slimSx: {
      minWidth: "120px",
      fontSize: theme.spacing(1.5),
      maxWidth: "350px",
    },
    defaultVariant: "outlined",
  },
  CURRENCY_RANGE: {
    nameSuffix: "values",
    slimVariant: "standard",
    slimSx: { minWidth: "90px", fontSize: theme.spacing(1.5) },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  NUMBER_RANGE: {
    nameSuffix: "values",
    slimVariant: "standard",
    slimSx: { minWidth: "100px", fontSize: theme.spacing(1.5) },
    slimSlotProps: {
      input: { sx: { fontSize: "12px", alignItems: "stretch" } },
    },
  },
  BOOLEAN: {
    nameSuffix: "value",
  },
  BOOLEAN_TRUE: {
    nameSuffix: "value",
  },
  BOOLEAN_FALSE: {
    nameSuffix: "value",
  },
  IS_NULL: {
    nameSuffix: "value",
  },
  SAVED_SEARCH: {
    label: "Saved Search Name",
    nameSuffix: "value",
    slimVariant: "underlined",
    defaultVariant: "outlined",
    slimSx: { minWidth: "160px", fontSize: theme.spacing(1.5) },
  },
};

function TargetingFilter({
  control,
  index,
  targetGroupIndex,
  namePrefix,
  handleRemove,
  audienceFields,
  savedSearchFilters,
  clause,
  allAvailableTargetingFilters,
  firstDateFilterIndex,
  timezone,
  variant = "default",
}: {
  control: Control<TargetingForm>;
  index: number;
  targetGroupIndex: number;
  namePrefix: string;
  handleRemove: () => void;
  audienceFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  savedSearchFilters: ListAudienceSearchFiltersQuery["listAudienceSearchFilters"]["audienceSearchFilters"];
  clause: TargetingFormFilter;
  allAvailableTargetingFilters: Partial<FilterHierarchy>;
  firstDateFilterIndex?: number;
  timezone: string;
  variant?: "default" | "slim";
}): ReactElement {
  const { setValue, trigger } = useFormContext<TargetingForm>();

  const dateFilterPrefix = `targetGroups.${targetGroupIndex}.dateFilter`;

  const pathPrefix = `${namePrefix}.${index}`;

  const [previousOperation, setPreviousOperation] = useState(
    clause?.filterOperation
  );

  const [previousFieldType, setPreviousFieldType] = useState(clause?.fieldType);

  const filterCategory: keyof typeof allAvailableTargetingFilters = useWatch({
    control,
    name: `${pathPrefix}.filterCategory` as any,
  });

  const filterOperation = useWatch({
    control,
    name: `${pathPrefix}.filterOperation` as Path<TargetingForm>,
  });

  const customFieldName = useWatch({
    control,
    name: `${pathPrefix}.fieldName` as any,
  });

  const currentDateFilterKey = useWatch({
    control,
    name: `${dateFilterPrefix}.key` as Path<TargetingForm>,
  });

  const currentDateFilterStart = useWatch({
    control,
    name: `${dateFilterPrefix}.startDate` as Path<TargetingForm>,
  });

  const currentDateFilterEnd = useWatch({
    control,
    name: `${dateFilterPrefix}.endDate` as Path<TargetingForm>,
  });

  const availableOperations = useMemo(
    () =>
      (filterCategory === "customField"
        ? customFieldOperations(audienceFields)[customFieldName]
        : allAvailableTargetingFilters[filterCategory]?.items) || [],
    [
      filterCategory,
      audienceFields,
      customFieldName,
      allAvailableTargetingFilters,
    ]
  );

  const selectedOperation = useMemo(
    () => availableOperations.find(op => op.value === filterOperation),
    [availableOperations, filterOperation]
  );

  const values = useWatch({
    control,
    name: `${pathPrefix}.values` as Path<TargetingForm>,
  });
  const staticValues = JSON.stringify(values);

  const value = useWatch({
    control,
    name: `${pathPrefix}.value` as Path<TargetingForm>,
  });

  useEffect(() => {
    if (filterOperation === "isInSavedSearch" && value !== "") {
      const searchBody = savedSearchFilters.find(
        f => f.id === value
      )?.searchBody;

      setValue(
        `${namePrefix}.${index}.filterConfig.searchBody` as Path<TargetingForm>,
        TargetingFormSchema.parse(searchBody)
      );
    }
  }, [value, setValue, filterOperation, namePrefix, index, savedSearchFilters]);

  useEffect(() => {
    void trigger(`${namePrefix}.${index}.values` as Path<TargetingForm>);
  }, [index, namePrefix, trigger, staticValues]);

  const resetValues = useCallback(
    (shouldDirty = false) => {
      const isPreviousRange =
        previousOperation?.toLocaleLowerCase().indexOf("between") > -1;
      setValue(`${pathPrefix}.value` as Path<TargetingForm>, "", {
        shouldDirty,
      });

      if (isPreviousRange) {
        // For ranges we need to set the individual array entries to empty string,
        // otherwise the UI will still show the previous values.
        setValue(`${pathPrefix}.values[0]` as Path<TargetingForm>, "", {
          shouldDirty,
        });
        setValue(`${pathPrefix}.values[1]` as Path<TargetingForm>, "", {
          shouldDirty,
        });
      } else {
        setValue(`${pathPrefix}.values` as Path<TargetingForm>, [], {
          shouldDirty,
        });
        setValue(
          `${pathPrefix}.filterConfig.centerZips` as Path<TargetingForm>,
          [""]
        );
        setValue(
          `${pathPrefix}.filterConfig.radius` as Path<TargetingForm>,
          ""
        );
      }
    },
    [previousOperation, setValue, pathPrefix]
  );

  const selectedOperationDefined = Boolean(selectedOperation);

  useEffect(() => {
    if (!selectedOperationDefined) {
      setValue(`${pathPrefix}.filterOperation` as Path<TargetingForm>, "");
      setValue(`${pathPrefix}.fieldType` as Path<TargetingForm>, "");
      resetValues();
      return;
    }

    if (selectedOperation?.fieldType) {
      if (selectedOperation.fieldType !== previousFieldType) {
        setValue(
          `${pathPrefix}.fieldType` as Path<TargetingForm>,
          selectedOperation.fieldType,
          { shouldDirty: true }
        );
        setPreviousFieldType(
          selectedOperation.fieldType as AudienceTargetingFieldType
        );
      }

      if (selectedOperation.value !== previousOperation) {
        resetValues(true);
      }

      if (selectedOperation.fieldType.startsWith("BOOLEAN_")) {
        setValue(
          `${pathPrefix}.value` as any,
          selectedOperation.fieldType === "BOOLEAN_TRUE" ? true : false,
          { shouldDirty: true }
        );
      }
      void trigger(`${pathPrefix}` as Path<TargetingForm>);
    }

    if (selectedOperation?.allowDateFiltering && !currentDateFilterKey) {
      setValue(`${dateFilterPrefix}.key` as Path<TargetingForm>, "ALL_TIME", {
        shouldDirty: true,
      });
      setValue(
        `${dateFilterPrefix}.startDate` as Path<TargetingForm>,
        DATE_FILTER_RANGE_BY_KEY["ALL_TIME"].start as null,
        { shouldDirty: true }
      );
      setValue(
        `${dateFilterPrefix}.endDate` as Path<TargetingForm>,
        DATE_FILTER_RANGE_BY_KEY["ALL_TIME"].end as null,
        { shouldDirty: true }
      );
    }

    if (!selectedOperation?.allowDateFiltering && !currentDateFilterKey) {
      setValue(dateFilterPrefix as Path<TargetingForm>, null, {
        shouldDirty: true,
      });
    }

    setPreviousOperation(filterOperation as AudienceTargetingFilterOperation);
  }, [
    filterOperation,
    filterCategory,
    setValue,
    namePrefix,
    index,
    previousOperation,
    currentDateFilterKey,
    selectedOperationDefined,
    selectedOperation?.fieldType,
    selectedOperation?.allowDateFiltering,
    selectedOperation?.value,
    staticValues,
    targetGroupIndex,
    dateFilterPrefix,
    setPreviousOperation,
    previousFieldType,
    pathPrefix,
    resetValues,
    trigger,
  ]);

  const isSlim = variant === "slim" || filterCategory === "savedSearch";

  function renderSelectedOperation() {
    if (!selectedOperation) {
      return null;
    }

    const fieldType = selectedOperation.fieldType as AudienceTargetingFieldType;

    const coreProps = {
      control,
      fullWidth: !isSlim,
      bottomLabel: isSlim
        ? undefined
        : FIELD_TYPE_TO_INPUT[fieldType]?.bottomLabel,
      name: `${namePrefix}.${index}.${FIELD_TYPE_TO_INPUT[fieldType]?.nameSuffix}` as Path<TargetingForm>,
      label: FIELD_TYPE_TO_INPUT[fieldType]?.label,
      sx: isSlim
        ? FIELD_TYPE_TO_INPUT[fieldType]?.slimSx
        : FIELD_TYPE_TO_INPUT[fieldType]?.defaultSx,
      slotProps: isSlim
        ? FIELD_TYPE_TO_INPUT[fieldType]?.slimSlotProps
        : undefined,
    };

    const inputVariant = isSlim
      ? FIELD_TYPE_TO_INPUT[fieldType]?.slimVariant
      : FIELD_TYPE_TO_INPUT[fieldType]?.defaultVariant;

    const enumValueOptions: string[] = customFieldName
      ? (audienceFields.find(f => f.name === customFieldName)?.typeParameters
          ?.values ?? [])
      : [];

    return (
      <>
        <input
          type="hidden"
          {...control.register(
            `${namePrefix}.${index}.fieldType` as Path<TargetingForm>
          )}
        />
        {selectedOperation?.allowDateFiltering &&
          currentDateFilterKey === "CUSTOM_DATE_RANGE" &&
          currentDateFilterEnd &&
          currentDateFilterStart && (
            <FlexBox direction="row" gap={2}>
              <RhfDatePicker<TargetingForm>
                name={`${dateFilterPrefix}.startDate` as Path<TargetingForm>}
                label="Start Date"
                control={control}
                fullWidth={!isSlim}
                timezone={timezone}
                disabled={
                  firstDateFilterIndex !== index &&
                  firstDateFilterIndex !== undefined
                }
                maxDate={
                  currentDateFilterEnd
                    ? DateTime.fromJSDate(
                        currentDateFilterEnd as unknown as Date
                      )
                    : undefined
                }
                isSlim={isSlim}
                backgroundColor={isSlim ? palette.lightGray : palette.white}
                inputLabelProps={
                  isSlim
                    ? {
                        sx: { mt: 1, mb: -1 },
                      }
                    : undefined
                }
              />
              <RhfDatePicker<TargetingForm>
                name={`${dateFilterPrefix}.endDate` as Path<TargetingForm>}
                label="End Date"
                control={control}
                fullWidth={!isSlim}
                timezone={timezone}
                disabled={
                  firstDateFilterIndex !== index &&
                  firstDateFilterIndex !== undefined
                }
                minDate={
                  currentDateFilterStart
                    ? DateTime.fromJSDate(
                        currentDateFilterStart as unknown as Date
                      )
                    : undefined
                }
                isSlim={isSlim}
                backgroundColor={isSlim ? palette.lightGray : palette.white}
                inputLabelProps={
                  isSlim
                    ? {
                        sx: { mt: 1, mb: -1 },
                      }
                    : undefined
                }
              />
            </FlexBox>
          )}
        {selectedOperation.fieldType === "STATE" && (
          <RhfSelect
            {...coreProps}
            variant={inputVariant as "outlined" | "underlined"}
            multiple={true}
            renderValue={selectedValues => (
              <>{(selectedValues as string[]).join(", ")}</>
            )}
          >
            {STATES.map(state => (
              <MenuItem key={state.abbreviation} value={state.abbreviation}>
                <Checkbox
                  checked={values.includes(state.abbreviation)}
                  icon={<CheckBoxUnchecked />}
                />
                {state.name}
              </MenuItem>
            ))}
          </RhfSelect>
        )}
        {selectedOperation.fieldType === "NUMBER" && (
          <RhfNumberField
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
          />
        )}
        {selectedOperation.fieldType === "NUMBER_RANGE" && (
          <RhfNumericalRange
            lowLabel="Lower Limit"
            highLabel="Upper Limit"
            fieldKey={`${filterOperation}-${index}`}
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
            bottomLabelSx={isSlim ? { mt: 0 } : {}}
          />
        )}
        {selectedOperation.fieldType === "CURRENCY" &&
          filterCategory === "donationHistory" && (
            <RhfCurrencyField
              {...coreProps}
              adornmentOffset={isSlim ? 0 : undefined}
              variant={inputVariant as TextFieldProps["variant"]}
            />
          )}
        {selectedOperation.fieldType === "TEXT" && (
          <RhfTextField
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
          />
        )}
        {selectedOperation.fieldType === "ZIP" && (
          <RhfCommaSeparatedTextField
            placeholder="12345, 67890, 54321"
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
          />
        )}
        {selectedOperation.fieldType === "ZIP_RADIUS" && (
          <RhfZipCodeRadius<TargetingForm>
            control={control}
            centerZipsName={
              `${namePrefix}.${index}.filterConfig.centerZips` as Path<TargetingForm>
            }
            radiusName={
              `${namePrefix}.${index}.filterConfig.radius` as Path<TargetingForm>
            }
            valuesName={`${namePrefix}.${index}.values` as Path<TargetingForm>}
            isSlim={isSlim}
          />
        )}
        {selectedOperation.fieldType === "CURRENCY_RANGE" && (
          <RhfNumericalRange
            lowLabel="Lower Limit"
            highLabel="Upper Limit"
            {...coreProps}
            variant={inputVariant as TextFieldProps["variant"]}
            inputSx={coreProps.sx}
            adornmentOffset={isSlim ? 0 : undefined}
            styleVariant="currency"
            bottomLabelSx={isSlim ? { mt: 0 } : {}}
          />
        )}
        {selectedOperation.fieldType.startsWith("BOOLEAN_") && (
          <input
            type="hidden"
            {...control.register(
              `${namePrefix}.${index}.value` as Path<TargetingForm>
            )}
          />
        )}
        {selectedOperation.fieldType === "ENUM" && (
          <RhfSelect
            {...coreProps}
            variant={inputVariant as "outlined" | "underlined"}
            withNoneOption={false}
            multiple
            renderValue={selectedValues => (
              <>{(selectedValues as string[]).join(", ")}</>
            )}
          >
            {enumValueOptions.map(option => (
              <MenuItem key={option} value={option}>
                <Checkbox
                  checked={values.includes(option)}
                  icon={<CheckBoxUnchecked />}
                />
                {option}
              </MenuItem>
            ))}
          </RhfSelect>
        )}
        {selectedOperation.fieldType === "SAVED_SEARCH" && (
          <>
            <RhfSelect
              {...coreProps}
              variant={inputVariant as "outlined" | "underlined"}
            >
              {savedSearchFilters.map(search => (
                <MenuItem key={`menu-item-${search.id}`} value={search.id}>
                  {search.name}
                </MenuItem>
              ))}
            </RhfSelect>
            <input
              type="hidden"
              {...control.register(
                `${namePrefix}.${index}.filterConfig.searchBody` as Path<TargetingForm>
              )}
            />
          </>
        )}
      </>
    );
  }

  return (
    <FlexBox direction="column" gap={2}>
      <FlexBox
        direction="row"
        gap={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <FlexBox
          gap={2}
          flexWrap={isSlim ? "wrap" : undefined}
          sx={{
            marginBottom: theme.spacing(1.5),
            "& .MuiInputLabel-root": {
              fontSize: theme.spacing(1.5),
            },
          }}
        >
          <Box>
            <RhfSelect
              name={
                `${namePrefix}.${index}.filterCategory` as Path<TargetingForm>
              }
              label="Filter Type"
              control={control}
              withNoneOption={false}
              sx={{ width: "140px", fontSize: theme.spacing(1.5) }}
              fullWidth
              variant="underlined"
              onChange={val => {
                if (val !== "customField") {
                  setValue(
                    `${namePrefix}.${index}.fieldName` as Path<TargetingForm>,
                    "",
                    { shouldDirty: true, shouldValidate: true }
                  );
                }

                // Auto-set operation for savedSearch category
                if (val === "savedSearch") {
                  setValue(
                    `${namePrefix}.${index}.filterOperation` as Path<TargetingForm>,
                    "isInSavedSearch",
                    { shouldDirty: true, shouldValidate: true }
                  );
                  setValue(
                    `${namePrefix}.${index}.fieldType` as Path<TargetingForm>,
                    "SAVED_SEARCH",
                    { shouldDirty: true }
                  );
                }

                resetValues(true);
              }}
            >
              {Object.keys(allAvailableTargetingFilters).map(ft => (
                <MenuItem key={ft} value={ft}>
                  {
                    allAvailableTargetingFilters[
                      ft as AudienceTargetingFilterCategory
                    ]!.label
                  }
                </MenuItem>
              ))}
            </RhfSelect>
          </Box>
          {filterCategory === "customField" && (
            <Box>
              <RhfSelect
                name={`${namePrefix}.${index}.fieldName` as Path<TargetingForm>}
                label="Custom Field"
                control={control}
                withNoneOption={false}
                sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
                fullWidth
                variant="underlined"
                onChange={newFieldName => {
                  const selectedField = audienceFields.find(
                    f => f.name === newFieldName
                  );
                  if (selectedField) {
                    setValue(
                      `${namePrefix}.${index}.fieldType` as Path<TargetingForm>,
                      selectedField.type
                    );

                    resetValues(true);
                  }
                }}
              >
                {audienceFields
                  .filter(field => field.isEditable)
                  .map(field => (
                    <MenuItem key={field.id} value={field.name}>
                      {field.name}
                    </MenuItem>
                  ))}
              </RhfSelect>
            </Box>
          )}

          {filterCategory !== "savedSearch" && (
            <Box>
              <RhfSelect
                name={
                  `${namePrefix}.${index}.filterOperation` as Path<TargetingForm>
                }
                label="Operation"
                control={control}
                withNoneOption={false}
                sx={{ minWidth: "140px", fontSize: theme.spacing(1.5) }}
                fullWidth
                variant="underlined"
                onChange={val => {
                  if (val === "isNull" || val === "customIsNull") {
                    setValue(
                      `${namePrefix}.${index}.value` as Path<TargetingForm>,
                      "true",
                      { shouldDirty: true, shouldValidate: true }
                    );
                  } else {
                    resetValues(true);
                  }
                }}
              >
                {availableOperations.map(op => (
                  <MenuItem key={op.label} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </RhfSelect>
            </Box>
          )}

          {selectedOperation?.allowDateFiltering && (
            <RhfSelect<TargetingForm>
              name={`${dateFilterPrefix}.key` as Path<TargetingForm>}
              label="Date Range"
              control={control}
              withNoneOption={false}
              sx={{
                minWidth: "140px",
                fontSize: theme.spacing(1.5),
              }}
              variant="underlined"
              disabled={
                firstDateFilterIndex !== index &&
                firstDateFilterIndex !== undefined
              }
              onChange={newValue => {
                const { start, end } =
                  DATE_FILTER_RANGE_BY_KEY[newValue as string] || {};
                setValue(
                  `${dateFilterPrefix}.startDate` as Path<TargetingForm>,
                  start?.toISO() ?? null,
                  { shouldDirty: true }
                );
                setValue(
                  `${dateFilterPrefix}.endDate` as Path<TargetingForm>,
                  end?.toISO() ?? null,
                  { shouldDirty: true }
                );
              }}
            >
              {DATE_FILTER_RANGES.map(range => (
                <MenuItem key={range.key} value={range.key}>
                  {range.label}
                </MenuItem>
              ))}
            </RhfSelect>
          )}
          {isSlim && renderSelectedOperation()}
        </FlexBox>
        <LinkButton
          variant="body3"
          sx={{ color: isSlim ? palette.darkGray : undefined }}
          onClick={() => handleRemove()}
        >
          Remove Filter
        </LinkButton>
      </FlexBox>

      {!isSlim && renderSelectedOperation()}
    </FlexBox>
  );
}

function SavedSearchFilterTargetingSummary({
  targetGroups,
}: {
  targetGroups: TargetingForm["targetGroups"];
}): ReactElement {
  const block = targetGroups.map((tg, i) => (
    <div key={`target-summary-${i}`}>
      <Typography variant="body2">
        {generateTargetGroupSentence(tg.clauses, "Subscribers who")}
      </Typography>
      {targetGroups.length - 1 != i && (
        <Typography variant="body2">OR</Typography>
      )}
    </div>
  ));
  return <>{block}</>;
}

export function TargetGroup({
  control,
  index,
  removeHandler,
  audienceFields,
  savedSearchFilters,
  allAvailableTargetingFilters,
  timezone,
  variant = "default",
}: {
  control: Control<TargetingForm>;
  index: number;
  removeHandler: () => void;
  audienceFields: GetAudienceContactFieldsQuery["getAudienceContactFields"];
  savedSearchFilters: ListAudienceSearchFiltersQuery["listAudienceSearchFilters"]["audienceSearchFilters"];
  allAvailableTargetingFilters: Partial<FilterHierarchy>;
  timezone: string;
  variant: "default" | "slim";
}): ReactElement {
  const { setValue } = useFormContext<TargetingForm>();
  const clausesData = useWatch({
    control,
    name: `targetGroups.${index}.clauses`,
  });

  const {
    fields: clauses,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `targetGroups.${index}.clauses`,
  });

  const [targetSentence, setTargetSentence] = useState<string>("");

  const savedSearchFilterIndex = useMemo(
    () =>
      clausesData.findIndex(clause => clause.filterCategory === "savedSearch"),
    [clausesData]
  );

  const isSlim = variant === "slim";

  useEffect(() => {
    if (clausesData) {
      const sentence = generateTargetGroupSentence(
        clausesData,
        "subscribers who"
      );
      const prefix = isSlim ? "" : " - ";
      const formattedSentence = isSlim ? upperFirst(sentence) : sentence;
      return sentence.length > 0
        ? setTargetSentence(prefix + formattedSentence)
        : setTargetSentence("");
    }
  }, [clausesData, isSlim]);

  const defaultFilter = {
    filterCategory: "location",
    filterOperation: null,
    fieldType: null,
    values: [],
  };

  const firstDateFilterIndex = clauses.findIndex(
    clause =>
      clause.filterCategory === "donationHistory" &&
      AudienceTargetingFilterDonationHistoryOperations.includes(
        clause.filterOperation as any
      )
  );

  useEffect(() => {
    // If the last date-based filter is removed, clear out the form value
    if (firstDateFilterIndex < 0) {
      setValue(`targetGroups.${index}.dateFilter`, null);
    }
  }, [firstDateFilterIndex, setValue, index]);

  const leftPadding = isSlim ? 2 : 3;

  return (
    <FlexBox
      direction="column"
      sx={isSlim ? { border: `1px solid ${palette.midGray}` } : {}}
    >
      <FlexBox
        justifyContent="space-between"
        sx={{
          backgroundColor: palette.midGray,
        }}
      >
        <Typography variant="body2" sx={{ p: 1, pl: leftPadding }}>
          <strong style={{ fontWeight: isSlim ? 600 : undefined }}>
            Filter Group {index + 1}
          </strong>{" "}
          - subscribers who meet all of the following filters
        </Typography>
        <IconButton onClick={removeHandler} aria-label="delete target">
          <Delete sx={{ height: "16px" }} />
        </IconButton>
      </FlexBox>
      {clauses.map((clause, idx) => (
        <FlexBox
          key={clause.id}
          flexDirection="column"
          sx={{
            backgroundColor: palette.lightGray,
            p: 1.4,
            pl: leftPadding,
            marginBottom: isSlim ? undefined : "2px",
            borderBottom: isSlim ? `1px solid ${palette.midGray}` : undefined,
          }}
        >
          <TargetingFilter
            control={control}
            index={idx}
            targetGroupIndex={index}
            namePrefix={`targetGroups.${index}.clauses`}
            handleRemove={() => remove(idx)}
            audienceFields={audienceFields}
            savedSearchFilters={savedSearchFilters}
            clause={clause}
            allAvailableTargetingFilters={
              savedSearchFilterIndex < 0
                ? idx > 0
                  ? omit(allAvailableTargetingFilters, "savedSearch")
                  : allAvailableTargetingFilters
                : savedSearchFilterIndex === idx
                  ? allAvailableTargetingFilters
                  : omit(allAvailableTargetingFilters, "savedSearch")
            }
            firstDateFilterIndex={
              firstDateFilterIndex > -1 ? firstDateFilterIndex : undefined
            }
            timezone={timezone}
            variant={variant}
          />
        </FlexBox>
      ))}

      {savedSearchFilterIndex > -1 ? (
        <FlexBox
          direction="column"
          gap={1}
          sx={{
            backgroundColor: palette.lightGray,
            padding: theme.spacing(1.4, leftPadding),
            marginBottom: isSlim ? undefined : "2px",
            fontSize: theme.spacing(1.5),
            minWidth: "auto",
            height: isSlim ? "30px" : undefined,
          }}
        >
          <SavedSearchFilterTargetingSummary
            targetGroups={
              clausesData[0].filterConfig?.searchBody?.targetGroups || []
            }
          />
        </FlexBox>
      ) : (
        <FlexBox
          gap={2}
          alignItems="center"
          flexDirection={isSlim ? "row-reverse" : "row"}
          justifyContent={isSlim ? "space-between" : undefined}
          sx={{
            backgroundColor: palette.lightGray,
            padding: theme.spacing(1.4, leftPadding),
            marginBottom: isSlim ? undefined : "2px",
          }}
        >
          <Button
            variant="contained"
            color="tertiary"
            sx={{
              fontSize: isSlim ? "10px" : "12px",
              backgroundColor: palette.midGray,
              height: isSlim ? "30px" : undefined,
              minWidth: "auto",
            }}
            onClick={() =>
              append(defaultFilter as unknown as TargetingFormFilter, {
                shouldFocus: false,
              })
            }
          >
            + Add Filter
          </Button>
          {targetSentence && (
            <Typography variant="body2">
              {!isSlim && <strong>Filter Group {index + 1}</strong>}{" "}
              {targetSentence}
            </Typography>
          )}
        </FlexBox>
      )}
    </FlexBox>
  );
}

export function TargetGroups({
  control,
  audienceId,
  timezone,
  targetGroups,
  removeGroup,
  variant = "default",
  savedSearchesEnabled = false,
}: {
  control: Control<TargetingForm>;
  audienceId: string;
  timezone: string;
  targetGroups: (TargetingForm["targetGroups"][number] & { id: string })[];
  removeGroup: (index?: number) => void;
  variant?: "default" | "slim";
  savedSearchesEnabled?: boolean;
}): ReactElement {
  const { subaccount, donationHistoryPageEnabled } = useSubaccount()!;

  const includeDonationHistory = donationHistoryPageEnabled || false;
  const includeCustomFields = useFeatureFlag("audienceCustomFields");

  const allAvailableTargetingFilters = availableTargetingFilters({
    includeDonationHistory,
    includeCustomFields,
    includeSavedSearches: savedSearchesEnabled,
  });

  const {
    data: customFields,
    loading,
    error,
  } = useQuery<
    GetAudienceContactFieldsQuery,
    GetAudienceContactFieldsQueryVariables
  >(GET_AUDIENCE_CONTACT_FIELDS, {
    variables: {
      input: {
        audienceId,
      },
    },
    skip: !includeCustomFields,
  });

  const {
    data: savedSearchesData,
    loading: savedSearchesLoading,
    error: savedSearchesError,
  } = useQuery(LIST_AUDIENCE_SEARCH_FILTERS, {
    variables: {
      input: {
        audienceId,
        subaccountId: subaccount.id,
        orderBy: "NAME_ASC",
      },
    },
    skip: !audienceId || !savedSearchesEnabled,
  });

  return (
    <AsyncWrapper
      loading={[loading, savedSearchesLoading]}
      errors={[error, savedSearchesError]}
    >
      <FlexBox direction="column" sx={{ marginBottom: theme.spacing(3) }}>
        {targetGroups.map((targetGroup, index) => {
          return (
            <div key={targetGroup.id}>
              {index > 0 && (
                <Typography
                  variant="body1Bold"
                  sx={{ margin: theme.spacing(2, 0) }}
                  component={"div"}
                >
                  OR...
                </Typography>
              )}
              <TargetGroup
                control={control}
                index={index}
                removeHandler={() => removeGroup(index)}
                allAvailableTargetingFilters={allAvailableTargetingFilters}
                audienceFields={customFields?.getAudienceContactFields || []}
                savedSearchFilters={
                  savedSearchesData?.listAudienceSearchFilters
                    ?.audienceSearchFilters || []
                }
                timezone={timezone}
                variant={variant}
              />
            </div>
          );
        })}
      </FlexBox>
    </AsyncWrapper>
  );
}
